import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { perDayMax, useSite } from '../site/SiteProvider';
import { PROVIDER_LABELS, passwordEnabled, socialProviders, type AuthProviderId } from '../auth/config';

export type EmailMode = 'signin' | 'signup' | 'reset';

interface Props {
  readonly busy: boolean;
  readonly error: string;
  readonly notice: string;
  readonly onPickSocial: (provider: AuthProviderId) => void;
  readonly onEmail: (mode: EmailMode, input: { email: string; password: string; displayName: string }) => void;
  readonly onClose: () => void;
}

/** ボタンに出す印。画像を足さずに済むよう、文字だけで区別する（色は index.css）。 */
const MARKS: Record<AuthProviderId, string> = { google: 'G', line: 'L', password: '✉', twitter: '𝕏', github: 'GH', facebook: 'f' };

const TITLES: Record<EmailMode, string> = { signin: 'メールアドレスでログイン', signup: 'メールアドレスで新規登録', reset: 'パスワードの再設定' };

/**
 * ログインの入口（doc/AUTH.md §5）。出す顔ぶれは `VITE_AUTH_PROVIDERS`。
 *
 * 上がソーシャルアカウント（ポップアップ）、下がメールアドレスとパスワード。
 * どれで入っても診断は同じように使える。管理者になれるのは Google で入った人だけなので、そこだけ断っておく。
 *
 * 利用規約とプライバシーポリシーは、入る前に読めるようリンクを出す（新しいタブで開く）。
 * 同意そのものは、入ったあとの ConsentDialog がチェックボックスで取る（doc/AUTH.md §13）。
 * ここで先にチェックさせないのは、すでに同意している人に毎回聞かないため。
 */
export function SignInDialog({ busy, error, notice, onPickSocial, onEmail, onClose }: Props) {
  const { limits } = useSite();
  const [mode, setMode] = useState<EmailMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onEmail(mode, { email, password, displayName });
  };

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="signin-title">
      <div className="modal__panel modal__panel--narrow">
        <h2 id="signin-title" className="modal__title">{passwordEnabled && mode !== 'signin' ? TITLES[mode] : 'ログイン'}</h2>
        <p className="modal__lead">
          ログインすると、作った診断にキャラクターの絵が付き（{perDayMax(limits.userImageDailyLimit)}）、自分の診断を管理できます。診断を受けるだけならログインは要りません。
        </p>

        {socialProviders.length > 0 && (
          <ul className="providers">
            {socialProviders.map((id) => (
              <li key={id}>
                <button className="provider" type="button" onClick={() => onPickSocial(id)} disabled={busy}>
                  <span className={`provider__mark provider__mark--${id}`} aria-hidden="true">{MARKS[id]}</span>
                  <span className="provider__label">{PROVIDER_LABELS[id]} でログイン</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {passwordEnabled && socialProviders.length > 0 && <p className="modal__divider"><span>または</span></p>}

        {passwordEnabled && (
          <form className="signin-form" onSubmit={submit}>
            {mode === 'signup' && (
              <label className="field">
                <span className="field__label">お名前（画面に出る表示名・任意）</span>
                <input className="field__input" value={displayName} maxLength={20} autoComplete="nickname"
                  onChange={(e) => setDisplayName(e.target.value)} disabled={busy} />
              </label>
            )}
            <label className="field">
              <span className="field__label">メールアドレス</span>
              <input className="field__input" type="email" value={email} required autoComplete="email"
                placeholder="you@example.co.jp" onChange={(e) => setEmail(e.target.value)} disabled={busy} />
            </label>
            {mode !== 'reset' && (
              <label className="field">
                <span className="field__label">パスワード{mode === 'signup' ? '（6 文字以上）' : ''}</span>
                <input className="field__input" type="password" value={password} required minLength={6}
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  onChange={(e) => setPassword(e.target.value)} disabled={busy} />
              </label>
            )}
            <button className="button button--primary signin-form__submit" type="submit" disabled={busy}>
              {busy ? '処理しています…' : mode === 'signin' ? 'ログイン' : mode === 'signup' ? '登録する' : '再設定メールを送る'}
            </button>
            <div className="signin-form__switch">
              {mode !== 'signin' && <button className="linklike" type="button" onClick={() => setMode('signin')} disabled={busy}>ログインに戻る</button>}
              {mode !== 'signup' && <button className="linklike" type="button" onClick={() => setMode('signup')} disabled={busy}>新規登録</button>}
              {mode !== 'reset' && <button className="linklike" type="button" onClick={() => setMode('reset')} disabled={busy}>パスワードを忘れた</button>}
            </div>
          </form>
        )}

        {notice && <p className="modal__notice" role="status">{notice}</p>}
        {error && <p className="modal__error" role="alert">{error}</p>}

        <p className="modal__fine">
          同じメールアドレスなら、あとから別のログイン方法を足しても同じアカウントとして繋がります。
          {socialProviders.includes('line') && ' LINE で入ったアカウントは、ほかの方法のアカウントとは別になります（LINE からはメールアドレスを受け取らないため）。'}
        </p>
        <p className="modal__fine">
          アカウントを作るときに、
          <Link to="/terms" target="_blank" rel="noreferrer">利用規約</Link>
          と
          <Link to="/privacy" target="_blank" rel="noreferrer">プライバシーポリシー</Link>
          へのご同意をお願いしています。
        </p>

        <div className="page__actions">
          <button className="button button--ghost" type="button" onClick={onClose} disabled={busy}>やめる</button>
        </div>
      </div>
    </div>
  );
}
